// OutboundCreateRuleModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { cleanString } from '../../../apiUtils';
import { normalizeRuleEvent } from '../../utils/integrationUtils';

// Constants
import { RULE_EVENT_OPTIONS } from '../../constants/integrationOptions';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * OutboundCreateRuleModal
 *
 * Renders the modal used to create a new outbound integration rule.
 */
const OutboundCreateRuleModal = ({
    isOpen,
    onClose,
    canManageCredentials,
    busyActionKey,
    draftName,
    draftEvent,
    draftTypeId,
    draftDestinationUrl,
    draftDestinationName,
    typeRows,
    onDraftNameChange,
    onDraftEventChange,
    onDraftTypeIdChange,
    onDraftDestinationUrlChange,
    onDraftDestinationNameChange,
    onCreateRule,
}) => {
  const isMobile = useIsMobile();
  const isCreating = busyActionKey === 'create-rule';

  return (
    <Modal
      title="Add Outbound Rule"
      isOpen={isOpen}
      width={isMobile ? '100%' : 620}
      maxHeight="85vh"
      onClose={onClose}
    >
      <ModalForm>
        <ModalField>
          <ModalHint hint="Configure when Ai should call an external webhook destination." />
        </ModalField>

        <ModalField>
          <ModalLabel label="Rule name" />
          <TextInput
            value={draftName}
            placeholder="Example: Tasks -> Zapier Sheet"
            disabled={!canManageCredentials}
            onChange={(event) => onDraftNameChange(event.target.value)}
          />
        </ModalField>

        <ModalField>
          <ModalLabel label="Trigger event" />
          <SelectInput
            value={draftEvent}
            disabled={!canManageCredentials}
            options={RULE_EVENT_OPTIONS}
            onChange={(event) => onDraftEventChange(normalizeRuleEvent(event.target.value))}
          />
        </ModalField>

        <ModalField>
          <ModalLabel label="Trigger type" />
          <SelectInput
            value={draftTypeId}
            disabled={!canManageCredentials || typeRows.length === 0}
            options={typeRows.map((row) => ({
              value: row.typeId,
              label: row.label,
            }))}
            onChange={(event) => onDraftTypeIdChange(cleanString(event.target.value))}
          />
        </ModalField>

        <ModalField>
          <ModalLabel label="Destination URL" />
          <TextInput
            value={draftDestinationUrl}
            placeholder="https://hooks.zapier.com/hooks/catch/..."
            disabled={!canManageCredentials}
            onChange={(event) => onDraftDestinationUrlChange(event.target.value)}
          />
        </ModalField>

        <ModalField>
          <ModalLabel label="Destination name (optional)" />
          <TextInput
            value={draftDestinationName}
            placeholder="Zapier Catch Hook"
            disabled={!canManageCredentials}
            onChange={(event) => onDraftDestinationNameChange(event.target.value)}
          />
        </ModalField>
      </ModalForm>

      <ModalButtons
        style={{
          justifyContent: 'space-between',
        }}
      >
        <ModalButtonSection align="START">
          <div />
        </ModalButtonSection>
        <ModalButtonSection align="END">
          <Button
            enabled={!isCreating}
            iconKey="close"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            enabled={canManageCredentials && !isCreating}
            iconKey="check"
            label={isCreating ? 'Creating...' : 'Create Rule'}
            onClick={onCreateRule}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
};

OutboundCreateRuleModal.displayName = 'OutboundCreateRuleModal';

export default OutboundCreateRuleModal;
